import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencyPicker from './CurrencyPicker';
import useNotifications from '../hooks/useNotifications';
import { Menu, X, User, LogOut, Home, MessageSquare, Calendar, BookOpen, Users, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
    const { user, isAuthenticated, logout } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [siteTitle, setSiteTitle] = useState('Methode 4 Life');
    const [customPages, setCustomPages] = useState([]);
    const [publicNav, setPublicNav] = useState({ events: true, library: true, membership: true, pillars: true });
    // Only poll when authenticated. The hook itself handles 401s silently, but avoid
    // an extra poll cycle for logged-out visitors.
    const { connectionsCount } = useNotifications();
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const loadSite = useCallback(async () => {
        try {
            const [s, p] = await Promise.all([
                axios.get(`${API_URL}/api/site-settings`),
                axios.get(`${API_URL}/api/pages`)
            ]);
            if (s.data?.site_title) setSiteTitle(s.data.site_title);
            setPublicNav({
                events: s.data?.public_nav_show_events !== false,
                library: s.data?.public_nav_show_library !== false,
                membership: s.data?.public_nav_show_membership !== false,
                pillars: s.data?.public_nav_show_pillars !== false
            });
            setCustomPages((p.data || []).filter(pg => pg.show_in_nav && pg.is_published !== false));
        } catch (err) {
            // silent — fall back to defaults
        }
    }, []);

    useEffect(() => {
        loadSite();
        const onChange = () => loadSite();
        window.addEventListener('pages-changed', onChange);
        window.addEventListener('site-settings-changed', onChange);
        return () => {
            window.removeEventListener('pages-changed', onChange);
            window.removeEventListener('site-settings-changed', onChange);
        };
    }, [loadSite]);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const isAdminLike = isAuthenticated && (user?.role === 'admin' || user?.membership_tier === 'partnership');

    // Custom pages filtered by scope. Default 'both' for older pages.
    const scopedPages = customPages.filter(p => {
        const scope = p.nav_scope || 'both';
        if (scope === 'both') return true;
        if (scope === 'public') return !isAuthenticated;
        if (scope === 'members') return isAuthenticated;
        return true;
    });

    const navLinks = [
        { name: t('nav.home'), path: '/', icon: Home },
        ...(isAuthenticated ? [
            { name: t('nav.dashboard'), path: '/dashboard', icon: Home },
            { name: 'Forum', path: '/forum', icon: MessageSquare, requiresMembership: true },
            { name: t('nav.events'), path: '/events', icon: Calendar },
            { name: t('nav.library'), path: '/library', icon: BookOpen, requiresMembership: true },
            { name: 'Connections', path: '/connections', icon: Users, requiresMembership: true },
            ...(publicNav.pillars ? [{ name: t('nav.pillars'), path: '/pillars', icon: Users }] : []),
        ] : [
            ...(publicNav.events ? [{ name: t('nav.events'), path: '/events', icon: Calendar }] : []),
            ...(publicNav.library ? [{ name: t('nav.library'), path: '/library', icon: BookOpen }] : []),
            ...(publicNav.pillars ? [{ name: t('nav.pillars'), path: '/pillars', icon: Users }] : []),
            ...(publicNav.membership ? [{ name: t('nav.membership'), path: '/membership', icon: Home }] : []),
        ]),
        ...scopedPages.map(p => ({ name: p.title, path: `/p/${p.slug}`, icon: BookOpen })),
        ...(isAdminLike ? [
            { name: 'Admin', path: '/admin', icon: Shield },
        ] : [])
    ];

    const filteredLinks = navLinks.filter(link => {
        if (link.requiresMembership && user && !user.membership_tier) {
            return false;
        }
        return true;
    });

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#E5E0D8]" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-6 sm:px-12">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center space-x-3" data-testid="nav-logo">
                        <span className="font-serif text-xl text-[#1A1A2E]">{siteTitle}</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-8">
                        {filteredLinks.map((link) => {
                            const showBadge = link.path === '/connections' && connectionsCount > 0;
                            return (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className="nav-link relative"
                                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                                >
                                    {link.name}
                                    {showBadge && (
                                        <span
                                            className="absolute -top-1 -right-4 min-w-[18px] h-[18px] px-1 bg-[#D4AF37] text-[#1A1A2E] text-[10px] font-medium rounded-full flex items-center justify-center"
                                            data-testid="connections-badge"
                                        >
                                            {connectionsCount > 9 ? '9+' : connectionsCount}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>

                    {/* Auth Buttons */}
                    <div className="hidden md:flex items-center space-x-4">
                        {/* LanguageSwitcher intentionally hidden — see /src/i18n/index.js */}
                        <CurrencyPicker />
                        {isAuthenticated ? (
                            <div className="flex items-center space-x-4">
                                {!isMobile && <NotificationBell />}
                                <Link
                                    to="/profile"
                                    className="flex items-center space-x-2 text-[#4A4A68] hover:text-[#5B2D8E] transition-colors"
                                    data-testid="nav-profile"
                                >
                                    <User className="w-5 h-5" />
                                    <span className="font-sans text-sm">{user?.name}</span>
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center space-x-2 text-[#4A4A68] hover:text-[#5B2D8E] transition-colors"
                                    data-testid="nav-logout"
                                >
                                    <LogOut className="w-5 h-5" />
                                </button>
                            </div>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="nav-link"
                                    data-testid="nav-login"
                                >
                                    {t('nav.sign_in')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="btn-primary text-xs px-6 py-2"
                                    data-testid="nav-register"
                                >
                                    {t('nav.join_the_circle')}
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile: bell + menu button */}
                    <div className="md:hidden flex items-center gap-2">
                        {isAuthenticated && isMobile && <NotificationBell />}
                        <button
                            className="relative text-[#1A1A2E]"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-[#E5E0D8] max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-menu">
                    <div className="px-6 py-4 space-y-4 pb-20">
                        {filteredLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className="flex items-center space-x-3 text-[#1A1A2E] py-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <link.icon className="w-5 h-5 text-[#5B2D8E]" />
                                <span>{link.name}</span>
                                {link.path === '/connections' && connectionsCount > 0 && (
                                    <span
                                        className="min-w-[18px] h-[18px] px-1 bg-[#D4AF37] text-[#1A1A2E] text-[10px] font-medium rounded-full flex items-center justify-center"
                                        data-testid="mobile-connections-badge"
                                    >
                                        {connectionsCount > 9 ? '9+' : connectionsCount}
                                    </span>
                                )}
                            </Link>
                        ))}
                        <div className="pt-3 border-t border-[#E5E0D8]" />

                        {/* Currency picker — placed BEFORE auth buttons so the
                            dropdown has room to open below without being clipped
                            by the mobile drawer bottom edge. */}
                        <div className="flex items-center justify-between py-2">
                            <span className="text-xs uppercase tracking-widest text-[#4A4A68]">Currency</span>
                            <CurrencyPicker />
                        </div>

                        {isAuthenticated ? (
                            <>
                                <Link
                                    to="/profile"
                                    className="flex items-center space-x-3 text-[#1A1A2E] py-2"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <User className="w-5 h-5 text-[#5B2D8E]" />
                                    <span>Profile</span>
                                </Link>
                                <button
                                    onClick={() => {
                                        handleLogout();
                                        setMobileMenuOpen(false);
                                    }}
                                    className="flex items-center space-x-3 text-[#1A1A2E] py-2 w-full"
                                >
                                    <LogOut className="w-5 h-5 text-[#5B2D8E]" />
                                    <span>{t('nav.sign_out')}</span>
                                </button>
                            </>
                        ) : (
                            <div className="pt-4 space-y-3 border-t border-[#E5E0D8]">
                                <Link
                                    to="/login"
                                    className="block text-center py-2 text-[#5B2D8E]"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.sign_in')}
                                </Link>
                                <Link
                                    to="/register"
                                    className="block text-center btn-primary"
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    {t('nav.join_the_circle')}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
}
